import AnalyticsEvents from '@constants/analytics';
import { useSourcePage } from '@hooks/use-source-page';
import useAnalyticsContext from '@hooks/useAnalyticsContext';
import {
	selectedCategoryCodesAtom,
	selectedCategoryCodesResolutionFailedAtom,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import {
	crossCategoryMarketplaceSearchSelector,
	isCrossCategoryMarketplaceSearchActive,
} from '@redux/experiments/selectors/cross-category-marketplace-search';
import { useAppSelector } from '@redux/hooks';
import { buildVendorSearchInteractionCrossCategoryProperties } from '@utils/cross-category-tracking';
import { useAtomValue } from 'jotai';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo } from 'react';

export const useLocationHeaderTracking = (
	categoryCode: string,
): {
	handleLocationSelection: (location: {
		city: string;
		stateCode: string;
	}) => void;
	handleDropdownOpen: () => void;
	handleInputChange: (value: string) => void;
} => {
	const sourcePage = useSourcePage();
	const { track } = useAnalyticsContext();
	const isCrossCategoryActive = useAppSelector(
		isCrossCategoryMarketplaceSearchActive,
	);
	const experimentVariant = useAppSelector(
		crossCategoryMarketplaceSearchSelector,
	);
	const selectedCategoryCodes = useAtomValue(selectedCategoryCodesAtom);
	const selectedCategoryCodesResolutionFailed = useAtomValue(
		selectedCategoryCodesResolutionFailedAtom,
	);

	const crossCategoryProps = useMemo(
		() =>
			buildVendorSearchInteractionCrossCategoryProperties({
				isCrossCategoryActive,
				experimentVariant,
				categories: selectedCategoryCodes,
				selectedCategoriesResolutionFailed:
					selectedCategoryCodesResolutionFailed,
			}),
		[
			isCrossCategoryActive,
			experimentVariant,
			selectedCategoryCodes,
			selectedCategoryCodesResolutionFailed,
		],
	);

	const vendorCategoryCodeStr = useMemo(() => {
		if (!isCrossCategoryActive || selectedCategoryCodesResolutionFailed) {
			return categoryCode;
		}
		if (selectedCategoryCodes.length > 0) {
			return selectedCategoryCodes.join(',');
		}
		return categoryCode;
	}, [
		isCrossCategoryActive,
		selectedCategoryCodesResolutionFailed,
		selectedCategoryCodes,
		categoryCode,
	]);

	const handleLocationSelection = useCallback(
		(location: { city: string; stateCode: string }) => {
			track({
				event: AnalyticsEvents.VENDOR_SEARCH_INTERACTION,
				properties: {
					action: 'location_selected',
					sourcePage,
					sourceContent: 'search_geography',
					vendorMarket: `${location.city}, ${location.stateCode}`,
					vendorCategoryCode: vendorCategoryCodeStr,
					...crossCategoryProps,
				},
			});
		},
		[vendorCategoryCodeStr, sourcePage, track, crossCategoryProps],
	);

	const handleDropdownOpen = useCallback(() => {
		track({
			event: AnalyticsEvents.VENDOR_SEARCH_INTERACTION,
			properties: {
				action: 'click_in_search',
				sourcePage,
				sourceContent: 'search_geography',
				vendorCategoryCode: vendorCategoryCodeStr,
				...crossCategoryProps,
			},
		});
	}, [vendorCategoryCodeStr, sourcePage, track, crossCategoryProps]);

	const handleInputChange = useMemo(
		() =>
			debounce((searchString: string) => {
				track({
					event: AnalyticsEvents.VENDOR_SEARCH_INTERACTION,
					properties: {
						action: 'typing',
						sourcePage,
						sourceContent: 'search_geography',
						searchString,
						vendorCategoryCode: vendorCategoryCodeStr,
						...crossCategoryProps,
					},
				});
			}, 1000),
		[vendorCategoryCodeStr, sourcePage, track, crossCategoryProps],
	);

	useEffect(() => {
		return () => {
			handleInputChange.cancel();
		};
	}, [handleInputChange]);

	return {
		handleLocationSelection,
		handleDropdownOpen,
		handleInputChange,
	};
};
